import { navigate } from '../core/router';
import { esc, showErr } from './users-shared';
import { fetchTeams } from './teams-shared';
import { openModal, closeModal, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
/**
 * "Teams" tab content (superadmin-only — gated by `can('tab.teams')` at the call
 * site in users.ts). Lists teams with create / rename / delete actions, all backed
 * by the superadmin-only /api/teams endpoints.
 *
 * Delete is SOFT (server/teams.ts): the group is hidden, not dismantled — its users,
 * farms, apps and flow assignments stay attached. The "Show deleted" toggle lists the
 * hidden ones (greyed, with Restore instead of Edit/Delete) so the undo is one click.
 */
export async function renderTeamsTab(panel) {
    panel.innerHTML = `
        <div class="users-toolbar">
            <button class="add-user-btn" id="add-team-btn">${t('users.create_team_btn')}</button>
            <label class="teams-deleted-toggle">
                <input type="checkbox" id="teams-show-deleted" />
                <span>${t('users.show_deleted_teams')}</span>
            </label>
        </div>
        <div id="teams-table-wrap"></div>
    `;
    panel.querySelector('#add-team-btn').addEventListener('click', () => navigate({ page: 'users', teamForm: 'new' }));
    const showDeleted = panel.querySelector('#teams-show-deleted');
    showDeleted.addEventListener('change', () => void loadAndRenderTeams(panel));
    await loadAndRenderTeams(panel);
}
async function loadAndRenderTeams(panel) {
    const wrap = panel.querySelector('#teams-table-wrap');
    if (!wrap)
        return;
    const withDeleted = panel.querySelector('#teams-show-deleted')?.checked ?? false;
    let teams;
    try {
        teams = await fetchTeams(withDeleted);
    }
    catch {
        wrap.innerHTML = `<p class="empty">${t('users.failed_to_load_teams')}</p>`;
        return;
    }
    if (teams.length === 0) {
        wrap.innerHTML = `<p class="empty">${t('users.no_teams')}</p>`;
        return;
    }
    wrap.innerHTML = `
        <table class="users-table users-table--2col">
            <thead>
                <tr><th>${t('common.name')}</th><th></th></tr>
            </thead>
            <tbody id="teams-tbody"></tbody>
        </table>
    `;
    const tbody = panel.querySelector('#teams-tbody');
    for (const team of teams) {
        const tr = document.createElement('tr');
        tr.dataset['id'] = String(team.id);
        if (team.deleted)
            tr.className = 'team-row--deleted';
        const actions = team.deleted
            ? `<button class="tbl-btn restore-team-btn" data-id="${team.id}">${t('users.restore_team')}</button>`
            : `<button class="tbl-btn edit-team-btn" data-id="${team.id}">${t('common.edit')}</button>
               <button class="tbl-btn delete-team-btn" data-id="${team.id}">${t('common.delete')}</button>`;
        tr.innerHTML = `
            <td class="u-login">${esc(team.name)}${team.deleted
            ? ` <span class="team-deleted-badge">${t('users.team_deleted_badge')}</span>` : ''}</td>
            <td class="u-actions">${actions}</td>
        `;
        tbody.appendChild(tr);
    }
    tbody.addEventListener('click', (e) => {
        const target = e.target;
        const id = Number.parseInt(target.dataset['id'] ?? '', 10);
        if (!id)
            return;
        const team = teams.find((x) => x.id === id);
        if (!team)
            return;
        if (target.classList.contains('edit-team-btn')) {
            navigate({ page: 'users', teamId: id });
        }
        else if (target.classList.contains('delete-team-btn')) {
            showDeleteTeamConfirm(team, () => void loadAndRenderTeams(panel));
        }
        else if (target.classList.contains('restore-team-btn')) {
            void restoreTeam(team, target, () => void loadAndRenderTeams(panel));
        }
    });
}
function showDeleteTeamConfirm(team, onDone) {
    const modal = openModal(`
        <h2 class="modal__title">${t('users.delete_team_title')}</h2>
        <p class="modal__text">${t('users.delete_team_confirm', { name: `<strong>${esc(team.name)}</strong>` })}</p>
        <p class="modal__hint">${t('users.delete_team_soft_hint')}</p>
        <div id="td-error" class="modal__error" style="display:none"></div>
        <div class="modal__actions">
            <button type="button" class="modal__cancel" id="td-cancel">${t('common.cancel')}</button>
            <button type="button" class="modal__submit modal__submit--danger" id="td-confirm">${t('common.delete')}</button>
        </div>
    `);
    modal.querySelector('#td-cancel').addEventListener('click', closeModal);
    modal.querySelector('#td-confirm').addEventListener('click', async () => {
        const errorEl = modal.querySelector('#td-error');
        try {
            const res = await fetch(`/api/teams/${team.id}`, { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) {
                showModalErr(errorEl, data.error ?? t('users.failed_to_delete_team'));
                return;
            }
            closeModal();
            onDone();
        }
        catch {
            showErr(errorEl, t('users.network_error'));
        }
    });
}
/** Restore needs no confirmation (it only un-hides), but a failure — a live team took
 *  the name in the meantime — has to be readable, so the server's message is shown. */
async function restoreTeam(team, btn, onDone) {
    btn.disabled = true;
    try {
        const res = await fetch(`/api/teams/${team.id}/restore`, { method: 'POST' });
        const data = await res.json();
        if (!res.ok) {
            btn.disabled = false;
            alert(data.error ?? t('users.failed_to_restore_team'));
            return;
        }
        onDone();
    }
    catch {
        btn.disabled = false;
        alert(t('users.network_error'));
    }
}
